import { Card, Badge, Row, Col } from 'react-bootstrap';
import { FUND_TYPES, CARES_TYPES } from '../../constants/constants';
import { formatCurrency, formatPercent } from '../../utils/formatters';
import ReadOnlyCaresProgramOptions from '../reports/fundtypes/cares/ReadOnlyCaresProgramOptions';

const getCaresTypeLabel = (caresType) => {
  if (!caresType) {
    return '-';
  }

  const match = CARES_TYPES.find(ct => ct.value === caresType);
  return match?.label || caresType;
};

const SummaryItem = ({ label, value }) => (
  <Row className="mb-2">
    <Col md={4}>
      <strong>{label}</strong>
    </Col>
    <Col md={8}>{value}</Col>
  </Row>
);

const toFiniteNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const renderPercentBadge = (value) => {
  const parsed = toFiniteNumber(value);
  return <Badge bg={parsed !== null ? 'success' : 'secondary'}>{parsed !== null ? formatPercent(parsed) : '-'}</Badge>;
};

const renderCurrencyBadge = (value) => {
  const parsed = toFiniteNumber(value);
  return <Badge bg={parsed !== null && parsed > 0 ? 'success' : 'secondary'}>{parsed !== null ? formatCurrency(parsed) : '-'}</Badge>;
};

const SubmitBenefitConfirmationFundTypeRenderer = ({ state, formSnapshot, submissionPayload }) => {
  if (!formSnapshot?.fundType) {
    return null;
  }

  switch (formSnapshot.fundType) {
    case FUND_TYPES.CARES: {
      const incomeMeasurementTypeLabel = submissionPayload?.incomeMeasurementType || 'Income Measurement';
      const arrearsLiabilityAmount = toFiniteNumber(submissionPayload?.arrearsLiabilityAmount);
      const incomeMeasurementPercentage = toFiniteNumber(submissionPayload?.incomeMeasurementPercentage);
      const arrearage = toFiniteNumber(submissionPayload?.arrearage);
      const energyDiscount = toFiniteNumber(submissionPayload?.energyDiscount);
      const pledgeAmount = toFiniteNumber(submissionPayload?.pledgeAmount);

      return (
        <>
          <Card className="mt-3">
            <Card.Header>Submission Details</Card.Header>
            <Card.Body>
              <SummaryItem
                label="CARES Type"
                value={<span>{getCaresTypeLabel(formSnapshot.caresType)}</span>}
              />
              {arrearsLiabilityAmount !== null && (
                <SummaryItem
                  label="Arrears Liability Amount"
                  value={renderCurrencyBadge(arrearsLiabilityAmount)}
                />
              )}
              {incomeMeasurementPercentage !== null && (
                <SummaryItem
                  label={`${incomeMeasurementTypeLabel} Percentage`}
                  value={renderPercentBadge(incomeMeasurementPercentage)}
                />
              )}
              {arrearage !== null && (
                <SummaryItem
                  label="Arrearage"
                  value={renderPercentBadge(arrearage)}
                />
              )}
              {energyDiscount !== null && (
                <SummaryItem
                  label="Energy Discount"
                  value={renderPercentBadge(energyDiscount)}
                />
              )}
              {pledgeAmount !== null && (
                <SummaryItem
                  label="Total AMP Instant Grant Relief Amount"
                  value={renderCurrencyBadge(pledgeAmount)}
                />
              )}
              <SummaryItem
                label="Additional Information"
                value={<span>{submissionPayload?.additionalInformation || '-'}</span>}
              />
            </Card.Body>
          </Card>

          <Row className="mt-3">
            <Col>
              <ReadOnlyCaresProgramOptions
                tpaFlg={submissionPayload?.tpaFlg}
                tpaMonths={submissionPayload?.tpaMonths}
                tpaMonthlyInstallmentEst={submissionPayload?.tpaMonthlyInstallmentEst}
                weatherizationProgramInterest={submissionPayload?.weatherizationProgramInterest}
                referralSource={submissionPayload?.referralSource}
              />
            </Col>
          </Row>
        </>
      );
    }

    case FUND_TYPES.LIHEAP:
    {
      const incomeMeasurementTypeLabel = submissionPayload?.incomeMeasurementType || 'Income Measurement';
      const incomeMeasurementPercentage = toFiniteNumber(submissionPayload?.incomeMeasurementPercentage);
      const arrearage = toFiniteNumber(submissionPayload?.arrearage);
      const energyDiscount = toFiniteNumber(submissionPayload?.energyDiscount);
      const pledgeAmount = toFiniteNumber(submissionPayload?.pledgeAmount);

      return (
        <Card className="mt-3">
          <Card.Header>Submission Details</Card.Header>
          <Card.Body>
            {state === 'WA' && incomeMeasurementPercentage !== null && (
              <SummaryItem
                label={`${incomeMeasurementTypeLabel} Percentage`}
                value={renderPercentBadge(incomeMeasurementPercentage)}
              />
            )}
            {state === 'WA' && arrearage !== null && (
              <SummaryItem
                label="Arrearage"
                value={renderPercentBadge(arrearage)}
              />
            )}
            {state === 'WA' && energyDiscount !== null && (
              <SummaryItem
                label="Energy Discount"
                value={renderPercentBadge(energyDiscount)}
              />
            )}
            <SummaryItem
              label="Pledge Amount"
              value={renderCurrencyBadge(pledgeAmount)}
            />
            <SummaryItem
              label="Additional Information"
              value={<span>{submissionPayload?.additionalInformation || '-'}</span>}
            />
          </Card.Body>
        </Card>
      );
    }

    case FUND_TYPES.PPC: {
      const pledgeAmount = submissionPayload?.pledgeAmount || 0;
      const incomeMeasurementTypeLabel = submissionPayload?.incomeMeasurementType || 'Income Measurement';
      const incomeMeasurementPercentage = Number(submissionPayload?.incomeMeasurementPercentage);
      const arrearage = Number(submissionPayload?.arrearage);
      const energyDiscount = Number(submissionPayload?.energyDiscount);
      const hasIncomeMeasurementPercentage = Number.isFinite(incomeMeasurementPercentage);
      const hasArrearage = Number.isFinite(arrearage);
      const hasEnergyDiscount = Number.isFinite(energyDiscount);

      return (
        <>
          <Card className="mt-3">
            <Card.Header>Submission Details</Card.Header>
            <Card.Body>
              <SummaryItem
                label={`${incomeMeasurementTypeLabel} Percentage`}
                value={<Badge bg={hasIncomeMeasurementPercentage ? 'success' : 'secondary'}>{hasIncomeMeasurementPercentage ? formatPercent(incomeMeasurementPercentage) : '-'}</Badge>}
              />
              <SummaryItem
                label="Arrearage"
                value={<Badge bg={hasArrearage ? 'success' : 'secondary'}>{hasArrearage ? formatPercent(arrearage) : '-'}</Badge>}
              />
              <SummaryItem
                label="Energy Discount"
                value={<Badge bg={hasEnergyDiscount ? 'success' : 'secondary'}>{hasEnergyDiscount ? formatPercent(energyDiscount) : '-'}</Badge>}
              />
              <SummaryItem
                label="Pledge Amount"
                value={<Badge bg={pledgeAmount > 0 ? 'success' : 'secondary'}>{formatCurrency(pledgeAmount, '-')}</Badge>}
              />
              <SummaryItem
                label="Submission Type"
                value={<Badge bg={formSnapshot.submissionType === 'NEW' ? 'success' : 'warning'}>{formSnapshot.submissionType}</Badge>}
              />
              <SummaryItem
                label="Additional Information"
                value={<span>{submissionPayload?.additionalInformation || '-'}</span>}
              />
            </Card.Body>
          </Card>
        </>
      );
    }

    case FUND_TYPES.WINTERHELP: {
      const pledgeAmount = submissionPayload?.pledgeAmount || 0;
      return (
        <Card className="mt-3">
          <Card.Header>Winter Help Submission</Card.Header>
          <Card.Body>
            <SummaryItem
              label="Pledge Amount"
              value={<Badge bg={pledgeAmount ? 'success' : 'secondary'}>{formatCurrency(pledgeAmount, '-')}</Badge>}
            />
            <SummaryItem
              label="Additional Information"
              value={<span>{submissionPayload?.additionalInformation || '-'}</span>}
            />
          </Card.Body>
        </Card>
      );
    }

    case FUND_TYPES.OTHER:
    default: {
      const rawPledgeAmount = submissionPayload?.pledgeAmount;
      // Handle NaN, null, undefined - default to 0
      const pledgeAmount = (typeof rawPledgeAmount === 'number' && !Number.isNaN(rawPledgeAmount)) ? rawPledgeAmount : 0;
      const formattedAmount = formatCurrency(pledgeAmount, '-');
      const incomeMeasurementTypeLabel = submissionPayload?.incomeMeasurementType || 'Income Measurement';
      const incomeMeasurementPercentage = Number(submissionPayload?.incomeMeasurementPercentage);
      const hasIncomeMeasurementPercentage = Number.isFinite(incomeMeasurementPercentage);
      
      return (
        <Card className="mt-3">
          <Card.Header>Other Fund Submission</Card.Header>
          <Card.Body>
            <SummaryItem
              label="Fund Type Name"
              value={<span>{submissionPayload?.otherFundType || '-'}</span>}
            />
            <SummaryItem
              label={`${incomeMeasurementTypeLabel} Percentage`}
              value={<Badge bg={hasIncomeMeasurementPercentage ? 'success' : 'secondary'}>{hasIncomeMeasurementPercentage ? formatPercent(incomeMeasurementPercentage) : '-'}</Badge>}
            />
            <SummaryItem
              label="Pledge Amount"
              value={<Badge bg={pledgeAmount > 0 ? 'success' : 'secondary'}>{formattedAmount}</Badge>}
            />
            <SummaryItem
              label="Additional Information"
              value={<span>{submissionPayload?.additionalInformation || '-'}</span>}
            />
          </Card.Body>
        </Card>
      );
    }
  }
};

export default SubmitBenefitConfirmationFundTypeRenderer;
